import { CompanyStatusEnum, SimpleAnswerEnum } from '../../enums/entity.enum';
import { InvestmentProgram } from '../investment-program/investment-program';
import { Contact, Location } from '../entities/entities.model';
import {
  StoreModel,
  StoreModelDate,
  StoreModelType,
  UTILS,
} from '@aksia/infrastructure';
import { CoInvestmentTierEnum } from '../../enums/coInvestment.enum';
import { IManagementCompany } from '../../interfaces/company/company.interface';
import { RankEnum } from '../../enums/closedEnd.enum';

export class ManagementCompany
  extends StoreModel
  implements IManagementCompany
{
  /**
   * Retrieves the start date of a given management company.
   *
   * This function first checks if the company has an `inceptionDate` and converts it to a Date object.
   * If the `inceptionDate` is not available, it then looks into the company's investment programs,
   * retrieves the start dates of the associated funds, sorts them in ascending order, and returns the earliest date.
   *
   * @param company - The management company for which to retrieve the start date.
   * @returns The start date of the company or the earliest start date of its investment programs' funds, if available.
   */
  static readonly getStartDate = (
    company: ManagementCompany,
  ): Date | undefined => {
    let streamStartDate;
    if (!company) return;
    if (company?.firmInceptionDate) {
      streamStartDate = UTILS.DATE.toDate(company.firmInceptionDate);
    } else if (company?.inceptionDate) {
      streamStartDate = UTILS.DATE.toDate(company.inceptionDate);
    }
    return streamStartDate;
  };

  managementCompanyId: number | undefined = undefined;
  name: string | undefined = undefined;

  //#region <<< Section - Status >>>

  managerStatus: CompanyStatusEnum | undefined = undefined;
  isSensitive: boolean | undefined = undefined;
  key: boolean | undefined = undefined;

  //#endregion

  //#region <<< Section - Historical Info >>>

  formerNames: string | undefined = undefined;

  //#endregion

  //#region <<< Section - Manager Overview >>>

  managerOverview: string | undefined = undefined;

  //#endregion

  //#region <<< Section - Description >>>

  description: string | undefined = undefined;

  //#endregion

  //#region <<< Section - Short Name >>>

  shortName: string | undefined = undefined;

  //#endregion

  //#region <<< Section - AUM >>>

  isAumFirstMonth: boolean | undefined = undefined;
  isAumQuarterly: boolean | undefined = undefined;

  //#endregion

  //#region <<< Section - Audit >>>

  physicalFileName: string | undefined = undefined;
  dataSource: number | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  dataAsOfDate: Date | undefined = undefined;

  //#endregion

  //#region <<< Section - Basic Info >>>

  @StoreModelType(() => StoreModelDate)
  inceptionDate: Date | undefined = undefined;

  @StoreModelType(() => StoreModelDate)
  firmInceptionDate: Date | undefined = undefined;

  isAffiliate: boolean | undefined = undefined;
  independentSponsor: SimpleAnswerEnum | undefined = undefined;
  dealRanking: RankEnum | undefined = undefined;
  coInvestmentTier: CoInvestmentTierEnum | undefined = undefined;

  @StoreModelType(() => Location)
  addresses: Array<Location> = [];

  @StoreModelType(() => StoreModelDate)
  staffAsOf: Date | undefined = undefined;

  totalStaff: number | undefined = undefined;
  nonInvestmentStaff: number | undefined = undefined;
  generalEmail: string | undefined = undefined;
  url: string | undefined = undefined;

  legalRegion: string | undefined = undefined;
  $legalRegion: Array<number> = [];

  @StoreModelType(() => Contact)
  contacts: Array<Contact> = [];

  //#endregion

  //#region <<< Section - Program >>>

  @StoreModelType(() => InvestmentProgram)
  investmentPrograms: Array<InvestmentProgram> | undefined = [];

  //#endregion

  //#region <<< Section - Identification >>>

  managerCRD: number | undefined = undefined;
  managerCIK: number | undefined = undefined;

  //#endregion

  override $onTransform = () => {
    this.$legalRegion = this.legalRegion?.split(',')?.map((id) => +id) ?? [];
  };
}
